/**
 * BotSpot Web Dashboard - WebSocket Hook
 *
 * React hook for real-time bot status updates via WebSocket.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { apiClient } from "../api/client";
import type { Bot, BotStatus } from "../api/types";

/**
 * WebSocket message types
 */
interface StatusUpdateMessage {
  type: "status_update";
  bot_id: string;
  status: BotStatus;
  error_message?: string;
  last_active_at?: string;
  started_at?: string;
  stopped_at?: string;
  updated_at?: string;
}

interface ErrorMessage {
  type: "error";
  message: string;
}

type WebSocketMessage = StatusUpdateMessage | ErrorMessage;

/**
 * WebSocket connection states
 */
export enum WebSocketState {
  CONNECTING = "connecting",
  CONNECTED = "connected",
  DISCONNECTED = "disconnected",
  ERROR = "error",
}

/**
 * Hook return type
 */
interface UseWebSocketReturn {
  connectionState: WebSocketState;
  lastUpdate?: StatusUpdateMessage;
  error?: string;
}

/**
 * Get WebSocket URL for orchestrator
 *
 * WebSocket connections go directly to the orchestrator service,
 * not through the API gateway.
 */
function getWebSocketUrl(): string {
  // Use dedicated WebSocket URL or default to orchestrator's OrbStack URL
  const wsUrl = import.meta.env.VITE_WS_URL || "wss://orchestrator.botspot.orb.local";
  return wsUrl;
}

/**
 * Custom hook for WebSocket connection to a specific bot
 *
 * Establishes a WebSocket connection for real-time bot status updates.
 * Handles automatic reconnection on connection loss.
 *
 * @param botId - The bot ID to subscribe to (null to disable connection)
 * @param onUpdate - Callback when bot status updates
 * @returns Connection state and latest update
 *
 * @example
 * const { connectionState, lastUpdate } = useWebSocket(botId, (update) => {
 *   console.log('Bot status:', update.status);
 * });
 */
export function useWebSocket(
  botId: string | null,
  onUpdate?: (update: StatusUpdateMessage) => void
): UseWebSocketReturn {
  const [connectionState, setConnectionState] = useState<WebSocketState>(
    WebSocketState.DISCONNECTED
  );
  const [lastUpdate, setLastUpdate] = useState<StatusUpdateMessage>();
  const [error, setError] = useState<string>();

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const reconnectAttemptsRef = useRef(0);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  // Maximum reconnection attempts
  const MAX_RECONNECT_ATTEMPTS = 5;
  // Base reconnection delay in milliseconds
  const RECONNECT_BASE_DELAY = 1000;

  /**
   * Calculate reconnection delay with exponential backoff
   */
  const getReconnectDelay = useCallback((): number => {
    const delay = RECONNECT_BASE_DELAY * Math.pow(2, reconnectAttemptsRef.current);
    return Math.min(delay, 30000); // Cap at 30 seconds
  }, []);

  /**
   * Connect to WebSocket
   */
  const connect = useCallback(() => {
    if (!botId) return;

    const token = apiClient.getToken();
    if (!token) {
      setError("Authentication token not available");
      setConnectionState(WebSocketState.ERROR);
      return;
    }

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    try {
      const wsBaseUrl = getWebSocketUrl();
      const wsUrl = `${wsBaseUrl}/ws/bots/${botId}?token=${encodeURIComponent(token)}`;

      setConnectionState(WebSocketState.CONNECTING);
      setError(undefined);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnectionState(WebSocketState.CONNECTED);
        reconnectAttemptsRef.current = 0; // Reset reconnection counter
        setError(undefined);
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);

          if (message.type === "status_update") {
            setLastUpdate(message);
            onUpdateRef.current?.(message);
          } else if (message.type === "error") {
            setError(message.message);
          }
        } catch (err) {
          setError("Failed to parse WebSocket message");
        }
      };

      ws.onerror = () => {
        setConnectionState(WebSocketState.ERROR);
        setError("WebSocket connection error");
      };

      ws.onclose = () => {
        setConnectionState(WebSocketState.DISCONNECTED);
        wsRef.current = null;

        // Attempt reconnection with exponential backoff
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = getReconnectDelay();
          reconnectAttemptsRef.current += 1;

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else {
          setError("Maximum reconnection attempts reached");
          setConnectionState(WebSocketState.ERROR);
        }
      };
    } catch (err) {
      setConnectionState(WebSocketState.ERROR);
      setError("Failed to establish WebSocket connection");
    }
  }, [botId, getReconnectDelay]);

  /**
   * Disconnect WebSocket
   */
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setConnectionState(WebSocketState.DISCONNECTED);
    reconnectAttemptsRef.current = 0;
  }, []);

  /**
   * Effect: Connect when botId changes
   */
  useEffect(() => {
    if (botId) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [botId, connect, disconnect]);

  return {
    connectionState,
    lastUpdate,
    error,
  };
}

/**
 * Custom hook for WebSocket connection to multiple bots
 *
 * Useful for bot list views where you want to subscribe to multiple bots.
 * Note: Creates a separate WebSocket connection for each bot.
 *
 * @param botIds - Array of bot IDs to subscribe to
 * @param onUpdate - Callback when any bot status updates
 * @returns Map of bot IDs to connection states
 *
 * @example
 * const connections = useMultiWebSocket(botIds, (update) => {
 *   updateBot(update.bot_id, update);
 * });
 */
export function useMultiWebSocket(
  botIds: string[],
  onUpdate?: (update: StatusUpdateMessage) => void
): Record<string, UseWebSocketReturn> {
  const [connections, setConnections] = useState<Record<string, UseWebSocketReturn>>({});

  useEffect(() => {
    const newConnections: Record<string, UseWebSocketReturn> = {};

    botIds.forEach((botId) => {
      // This will be handled by individual useWebSocket hooks
      // For now, we'll use a simplified approach
      newConnections[botId] = {
        connectionState: WebSocketState.DISCONNECTED,
      };
    });

    setConnections(newConnections);
  }, [botIds]);

  return connections;
}

/**
 * Apply WebSocket update to a bot object
 *
 * Helper function to merge WebSocket status update with existing bot data.
 *
 * @param bot - Existing bot object
 * @param update - WebSocket status update
 * @returns Updated bot object
 */
export function applyBotUpdate(bot: Bot, update: StatusUpdateMessage): Bot {
  return {
    ...bot,
    status: update.status,
    error_message: update.error_message,
    last_active_at: update.last_active_at || bot.last_active_at,
    started_at: update.started_at || bot.started_at,
    stopped_at: update.stopped_at || bot.stopped_at,
    updated_at: update.updated_at || bot.updated_at,
  };
}
